import _extends from '@babel/runtime/helpers/extends';
import React__default, { useState, useContext } from 'react';
import styled, { css } from 'styled-components';
import { A as Alert } from '../../Alert/Alert-CeGIJhfJ.js';
import { I as Icon } from '../../Icon/Icon-AcXbY_t4.js';
import { f as focusOutline } from '../../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { F as FileUploadContext } from '../FileUploadContext-CI9_em1Z.js';
import { u as upload } from '../../LocalModules/storm-ui-icons/dist/esm/localIcons-9_jhfe7X-DyCR5FH7.js';

const transition = property => css(["@media (prefers-reduced-motion:no-preference){transition:", " 100ms ease-in-out;}"], property);
const Container = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-0"
})(["background:", ";display:", ";width:", ";cursor:", ";"], ({
  theme
}) => theme.dropZone.backgroundColor.default, ({
  $fullWidth
}) => $fullWidth ? 'block' : 'inline-block', ({
  $fullWidth
}) => $fullWidth ? '100%' : 'auto', ({
  disabled
}) => disabled && 'not-allowed');
const Label = /*#__PURE__*/styled('label').withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-1"
})(["border-width:", ";border-style:dashed;border-color:", ";border-radius:", ";cursor:pointer;width:100%;height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;margin-bottom:0;outline:none;box-sizing:border-box;padding:", ";position:relative;transition:all 0.3s;vertical-align:middle;", " &:focus-visible{", "}&:hover{", " border-color:", ";background:", ";&& > i{", " color:", ";}}", " ", ""], ({
  theme
}) => theme.border.width.default, ({
  theme
}) => theme.border.color.medium.default, ({
  theme
}) => theme.border.radius.medium, ({
  theme
}) => theme.dropZone.padding, transition('background'), focusOutline, transition('background'), ({
  theme
}) => theme.border.color.medium.hover, ({
  theme
}) => theme.dropZone.backgroundColor.hover, transition('color'), ({
  theme
}) => theme.icon.color.secondary, ({
  disabled
}) => disabled && css(["border-color:", ";background:", ";pointer-events:none;&& > p,div{color:", ";}&& > i{", " color:", ";}"], ({
  theme
}) => theme.border.color.medium.unavailable, ({
  theme
}) => theme.dropZone.backgroundColor.unavailable, ({
  theme
}) => theme.text.color.tertiary, transition('color'), ({
  theme
}) => theme.icon.color.unavailable), ({
  activeDrag
}) => activeDrag && css(["", " border-color:", ";background:", ";&& > i{", " color:", ";}"], transition('background'), ({
  theme
}) => theme.border.color.medium.hover, ({
  theme
}) => theme.dropZone.backgroundColor.hover, transition('color'), ({
  theme
}) => theme.icon.color.secondary));
const Overlay = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-2"
})(["position:absolute;width:100%;height:100%;border-radius:", ";top:0px;right:0px;bottom:0px;left:0px;"], ({
  theme
}) => theme.border.radius.medium);
const UploadIcon = /*#__PURE__*/styled(Icon).withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-3"
})(["color:", ";", ""], ({
  theme
}) => theme.icon.color.default, transition('color'));
const DropZoneTextContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-4"
})(["text-align:center;margin-block-start:", ";"], ({
  theme
}) => theme.dropZone.dropZoneTextContainer.marginTop);
const CriteriaListContainer = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-5"
})(["margin-block-start:", ";color:", ";"], ({
  theme
}) => theme.spacing.xxxsmall.default, ({
  theme
}) => theme.text.color.secondary);
const UploaderAlert = /*#__PURE__*/styled(Alert).withConfig({
  componentId: "sc-storm-ui-40027066__sc-mb1eqy-6"
})(["margin-top:", ";"], ({
  theme
}) => theme.dropZone.uploaderAlert.marginTop);
const FileUploadDropZone = ({
  heading,
  dropzoneId,
  onDrop
}) => {
  const [activeDrag, setActiveDrag] = useState(false);
  const {
    description,
    disabled,
    errorText,
    fullWidth,
    inputId,
    inputRef,
    onCloseAlert
  } = useContext(FileUploadContext);
  const dropZoneTextId = `${dropzoneId}-heading`;
  const criteriaListId = `${dropzoneId}-descriptions`;
  const handleKeyDown = event => {
    if (!disabled && event.key === 'Enter') {
      event.preventDefault();
      if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
        /** Reset the value of the input so
         * duplicate files are not ignored */
        inputRef.current.value = '';
        inputRef.current.click();
      }
    }
  };
  const handleDrag = event => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setActiveDrag(true);
    } else if (event.type === 'dragleave') {
      setActiveDrag(false);
    }
  };
  const handleDrop = event => {
    event.preventDefault();
    setActiveDrag(false);
    onDrop === null || onDrop === void 0 || onDrop(event);
  };
  const handleClick = event => {
    event.preventDefault();
    if (inputRef !== null && inputRef !== void 0 && inputRef.current) {
      /** Reset the value of the input so
       * duplicate files are not ignored */
      inputRef.current.value = '';
      inputRef.current.click();
    }
  };
  return /*#__PURE__*/React__default.createElement(Container, {
    $fullWidth: fullWidth,
    disabled: disabled,
    id: dropzoneId
  }, /*#__PURE__*/React__default.createElement(Label, _extends({
    activeDrag: activeDrag,
    disabled: disabled
  }, disabled && {
    'aria-disabled': true
  }, {
    htmlFor: inputId,
    onDragEnter: handleDrag,
    onKeyDown: handleKeyDown,
    onClick: handleClick,
    tabIndex: 0,
    "aria-labelledby": dropZoneTextId,
    "aria-describedby": criteriaListId
  }), /*#__PURE__*/React__default.createElement(UploadIcon, {
    type: upload,
    size: "large",
    "aria-hidden": true
  }), heading && /*#__PURE__*/React__default.createElement(DropZoneTextContainer, {
    id: dropZoneTextId
  }, heading), description && /*#__PURE__*/React__default.createElement(CriteriaListContainer, {
    id: criteriaListId
  }, description), activeDrag && /*#__PURE__*/React__default.createElement(Overlay, {
    onDragEnter: handleDrag,
    onDragLeave: handleDrag,
    onDragOver: handleDrag,
    onDrop: handleDrop
  })), errorText && /*#__PURE__*/React__default.createElement(UploaderAlert, {
    variant: "error",
    role: "alert",
    withCloseButton: true,
    onClick: onCloseAlert
  }, errorText));
};

export { FileUploadDropZone as F };
//# sourceMappingURL=FileUploadDropZone-BJPRag26.js.map
